import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { User } from "lucide-react";
import MessageForm from "../components/MessageForm";

const DEDICATION_TITLE = "Gracias por Acompañarme en Este Viaje Literario";
const DEDICATION = [
  {
    body:
      "Querido lector, querida lectora, si has llegado hasta aquí, significa que has decidido acompañarme en este rincón especial donde las palabras cobran vida y los sentimientos encuentran su hogar.",
  },
  {
    heading: "Mi Compromiso Contigo",
    body:
      "Cada escrito que encuentras aquí nace de una experiencia auténtica, de una emoción genuina o de un momento de inspiración que deseo compartir contigo. No hay palabras vacías en este espacio, solo verdades del corazón expresadas a través de la escritura.\n\nMi compromiso es mantener siempre la honestidad emocional en cada línea, porque creo que solo desde la autenticidad podemos crear conexiones reales entre el escritor y el lector.",
  },
  {
    heading: "Tu Presencia Importa",
    body:
      "Saber que hay alguien al otro lado leyendo estas palabras le da sentido completo a cada escrito. Tú, con tu lectura, das vida a estas ideas y las conviertes en experiencias compartidas.\n\nSi algún texto resuena contigo, si una palabra te inspira o si simplemente encuentras un momento de paz en estos escritos, entonces la magia de la literatura ha cumplido su propósito.",
  },
  {
    heading: "Una Invitación Personal",
    body:
      "Te invito a que no seas solo un observador pasivo de estas palabras. Permítete sentir, reflexionar, incluso disentir. Cada reacción tuya enriquece el diálogo silencioso que establecemos a través de la literatura.\n\nQue estas palabras sean compañía en tus momentos de soledad, inspiración en tus días grises, y celebración en tus alegrías. Porque al final, escribimos y leemos por la misma razón: para sentirnos menos solos en este hermoso y complejo mundo.",
  },
];

export default function Author() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/author");
        setProfile(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const photo = profile?.photo;
  const biography = profile?.biography || "";
  const featured = profile?.featured_text;

  return (
    <div
      className="max-w-5xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-32"
      data-testid="author-page"
    >
      <div
        className="text-xs uppercase tracking-[0.35em] mb-6"
        style={{ color: "var(--rincon-secondary)" }}
      >
        Sobre el autor
      </div>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
        <div className="md:col-span-4">
          {photo ? (
            <img
              src={photo}
              alt="Javilobo"
              data-testid="author-photo"
              className="w-full h-auto aspect-[3/4] object-cover"
              style={{ filter: "sepia(0.08)" }}
            />
          ) : (
            <div
              className="w-full aspect-[3/4] flex items-center justify-center border"
              style={{
                borderColor: "var(--rincon-border)",
                background: "var(--rincon-surface)",
              }}
              data-testid="author-photo-placeholder"
            >
              <User className="w-16 h-16" style={{ color: "var(--rincon-text-muted)" }} />
            </div>
          )}
        </div>

        <div className="md:col-span-8">
          <h1
            className="font-display font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95]"
            style={{ color: "var(--rincon-text)" }}
          >
            Javi <em className="italic">Lobo</em>
          </h1>

          {loading ? (
            <div
              className="mt-8 font-body italic"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              Cargando...
            </div>
          ) : biography ? (
            <div
              className="mt-8 prose-literary font-body whitespace-pre-wrap"
              style={{ color: "var(--rincon-text)", fontSize: "1.2rem" }}
              data-testid="author-biography"
            >
              {biography}
            </div>
          ) : (
            <p
              className="mt-8 font-body italic text-xl"
              style={{ color: "var(--rincon-text-muted)" }}
              data-testid="author-biography-empty"
            >
              (La biografía aún no ha sido escrita.)
            </p>
          )}
        </div>
      </div>

      {/* Featured text */}
      {featured ? (
        <section
          className="mt-24 pt-14 border-t"
          style={{ borderColor: "var(--rincon-border)" }}
          data-testid="author-featured"
        >
          <div
            className="text-xs uppercase tracking-[0.3em] mb-4"
            style={{ color: "var(--rincon-secondary)" }}
          >
            Un texto que el autor te recomienda
          </div>
          <Link to={`/texto/${featured.id}`} className="block group">
            <h2
              className="font-display text-3xl md:text-4xl tracking-tight"
              style={{ color: "var(--rincon-text)" }}
            >
              <span className="group-hover:text-[var(--rincon-primary)] transition-colors">
                {featured.title}
              </span>
            </h2>
            <div
              className="mt-2 text-xs uppercase tracking-[0.2em]"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              {featured.main_category} · {featured.subcategory}
            </div>
            <p
              className="mt-4 font-body italic max-w-3xl"
              style={{ color: "var(--rincon-text-muted)", fontSize: "1.15rem" }}
            >
              {featured.content.slice(0, 260)}
              {featured.content.length > 260 ? "…" : ""}
            </p>
            <span
              className="inline-block mt-4 font-display ink-link"
              style={{ color: "var(--rincon-primary)" }}
            >
              → Leer completo
            </span>
          </Link>
        </section>
      ) : null}

      {/* Dedication (fixed) */}
      <section
        className="mt-28 pt-16 border-t"
        style={{ borderColor: "var(--rincon-border)" }}
        data-testid="author-dedication"
      >
        <div
          className="font-display text-4xl text-center"
          style={{ color: "var(--rincon-secondary)" }}
          aria-hidden="true"
        >
          ❦
        </div>
        <h2
          className="mt-8 font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-tight text-center max-w-3xl mx-auto"
          style={{ color: "var(--rincon-text)" }}
        >
          {DEDICATION_TITLE}
        </h2>

        <div className="mt-14 max-w-2xl mx-auto space-y-10">
          {DEDICATION.map((block) => (
            <div key={block.heading || "intro"}>
              {block.heading ? (
                <h3
                  className="font-display text-2xl md:text-3xl mb-4"
                  style={{ color: "var(--rincon-primary)" }}
                >
                  {block.heading}
                </h3>
              ) : null}
              <p
                className="font-body whitespace-pre-wrap"
                style={{ color: "var(--rincon-text)", fontSize: "1.15rem", lineHeight: "1.85" }}
              >
                {block.body}
              </p>
            </div>
          ))}

          <div className="pt-6">
            <p
              className="font-body italic"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              Con cariño y gratitud infinita,
            </p>
            <p
              className="mt-3 font-display text-3xl"
              style={{ color: "var(--rincon-primary)" }}
            >
              Javi Lobo
            </p>
          </div>

          <div
            className="font-display text-4xl text-center pt-6"
            style={{ color: "var(--rincon-secondary)" }}
            aria-hidden="true"
          >
            ❦
          </div>
        </div>
      </section>

      <MessageForm textId={null} />
    </div>
  );
}
